// frontend/context/WebSocketContext.tsx

import React, { createContext, useEffect, useRef, useState } from 'react';
import { WebSocketService } from '../webSocketService';  // Removed ExtendedAppDispatch import
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { RootState } from '../store/store';
import store from '../store/store';  // Import the store directly
import { setUser } from '../store/slices/sessionSlice'; // Import setUser action

interface WebSocketContextType {
  sendToWs: (message: string) => void;
  sendBinaryToWs: (binaryData: Blob | ArrayBuffer | ArrayBufferView) => void;
  isConnected: boolean;
  initializeUser: () => void;
  switchToChat: (chatGroupId: string) => void;
  waitForConnection: () => Promise<void>;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0();
  const dispatch = useDispatch();
  const websocketServiceRef = useRef<WebSocketService | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const session = useSelector((state: RootState) => state.session);

  // Update session state with Auth0 user information
  useEffect(() => {
    // Enhanced condition: set user if authenticated and either no user_id exists OR user_id doesn't match Auth0 user
    const shouldSetUser = isAuthenticated && user?.sub && user?.name && 
                          (!session.user_id || session.user_id !== user.sub);
    
    if (shouldSetUser && user.sub && user.name) {
      console.log('🔐 WebSocketContext: Setting user information in session state:', { 
        id: user.sub, 
        name: user.name,
        currentSession: session,
        reason: !session.user_id ? 'no_user_id' : 'user_id_mismatch'
      });
      dispatch(setUser({
        id: user.sub,
        user_name: user.name,
        email: user.email || null
      }));
      console.log('🔐 WebSocketContext: User authentication dispatched successfully');
    } else {
      console.log('🔐 WebSocketContext: Skipping user authentication:', {
        isAuthenticated,
        hasUserSub: !!user?.sub,
        hasUserName: !!user?.name,
        hasExistingUserId: !!session.user_id,
        userIdMatches: session.user_id === user?.sub,
        currentSession: session
      });
      
      // WARNING: If we have a user_id but Auth0 is not authenticated, something might be wrong
      if (session.user_id && !isAuthenticated) {
        console.warn('🔐 WebSocketContext: WARNING - Session has user_id but Auth0 not authenticated:', {
          sessionUserId: session.user_id,
          isAuthenticated,
          auth0User: user
        });
        // Don't clear the session automatically - let the recovery mechanism handle it
      }
    }
  }, [isAuthenticated, user, session.user_id, dispatch]);

  useEffect(() => {
    const initWebSocket = async () => {
      const canConnect = isAuthenticated && !isLoading && user?.sub && user?.name && session.session_id;
      
      if (!websocketServiceRef.current && canConnect && user) {
        const websocketService = new WebSocketService(
          store,  // Ensure the store is correctly passed
          getAccessTokenSilently,
          { sub: user.sub as string, name: user.name as string },
          session.session_id as string
        );

        // Subscribe to connection status changes (connect/disconnect/reconnect)
        websocketService.setConnectionStatusListener((connected: boolean) => {
          setIsConnected(connected);
        });
        
        console.log('Initializing WebSocket connection...');
        await websocketService.connect();
        websocketServiceRef.current = websocketService;
        // isConnected will be set by the status listener when onopen fires
      }
    };

    initWebSocket();

    return () => {
      websocketServiceRef.current?.disconnect();
      websocketServiceRef.current = null;
      setIsConnected(false);
    };
  }, [getAccessTokenSilently, user, isAuthenticated, isLoading, session.session_id]);

  const sendToWs = (message: string) => {
    websocketServiceRef.current?.sendMessage(message);
  };

  const sendBinaryToWs = (binaryData: Blob | ArrayBuffer | ArrayBufferView) => {
    websocketServiceRef.current?.sendBinaryMessage(binaryData);
  };

  const initializeUser = () => {
    websocketServiceRef.current?.initializeUser();
  };

  const switchToChat = (chatGroupId: string) => {
    websocketServiceRef.current?.switchToChat(chatGroupId);
  };

  const waitForConnection = () => {
    if (websocketServiceRef.current) {
      return websocketServiceRef.current.waitForConnection();
    }
    return Promise.resolve();
  };

  // Re-initialize user after every successful (re)connection
  useEffect(() => {
    if (isConnected) {
      const timer = setTimeout(() => {
        websocketServiceRef.current?.initializeUser();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isConnected]);

  return (
    <WebSocketContext.Provider value={{ 
      sendToWs, 
      sendBinaryToWs, 
      isConnected, 
      initializeUser, 
      switchToChat,
      waitForConnection
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;